import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import AuthService from "../../../services/authService";
import { auth, setKeepSignedIn } from "../firebase.web";

/**
 * Web counterpart of contexts/AuthContext.js. Same surface, but built only on the shared
 * AuthService (plus onAuthStateChanged and the superAdmin custom claim).
 *
 * useAuth() ->
 *   user                 Firebase user spread with `.profile` (same shape as mobile), or null
 *   userProfile          users/{uid} doc data, or null
 *   loading              true until the first auth state has resolved
 *   emailVerified        user?.emailVerified === true
 *   isSuperAdmin         `superAdmin` custom claim === true (what the Firestore rules enforce)
 *   orphanedAccountMessage / clearOrphanedAccountMessage  set when a verified account has no profile doc
 *   signIn(email, password, keepSignedIn=true) -> { success, error }   (error is a message string)
 *   signUp(email, password, userData)          -> { success, error, uid, verificationEmailFailed }
 *   signOut()                                   -> { success, error }
 *   refreshProfile()                            -> re-reads users/{uid}
 *   checkVerification()                         -> { verified, error } (reloads the user when newly verified)
 *   sendEmailVerification()                     -> { success, error }
 * While signed in but unverified the context polls checkVerification every 3 s (like mobile).
 */
const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}

const UNEXPECTED = "An unexpected error occurred";

async function readSuperAdmin(fbUser) {
  try {
    const { claims } = await fbUser.getIdTokenResult();
    return claims.superAdmin === true;
  } catch {
    return false;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orphanedAccountMessage, setOrphanedAccountMessage] = useState(null);
  // True after a deliberate sign-out (until the next sign-in): App's signed-out redirect then
  // carries no `from`, so the next sign-in (maybe someone else's) never lands on the old page.
  const [signedOutByUser, setSignedOutByUser] = useState(false);
  const epoch = useRef(0);

  const clearAll = useCallback(() => {
    setUser(null);
    setUserProfile(null);
    setIsSuperAdmin(false);
  }, []);

  // Resolve the current Firebase user into { user+profile, claims } and apply it, unless a newer
  // call has started (stale async guard).
  const syncFromFirebase = useCallback(async () => {
    const my = ++epoch.current;
    const fb = auth.currentUser;
    if (!fb) {
      clearAll();
      return null;
    }
    // Stale once a newer sync started OR the signed-in account changed (e.g. another tab).
    const stale = () => my !== epoch.current || auth.currentUser?.uid !== fb.uid;
    const { user: u } = await AuthService.getCurrentUser();
    const superAdmin = await readSuperAdmin(fb);
    if (stale() || (u && u.uid !== fb.uid)) return null;
    if (!u) {
      clearAll();
      return null;
    }
    // Orphan guard: verified Auth account whose users/{uid} doc is gone. Indeterminate checks let the user in.
    if (u.emailVerified && !u.profile) {
      const { exists } = await AuthService.checkProfileExists(u.uid);
      if (stale()) return null; // never sign out an account other than the one checked
      if (exists === false) {
        await AuthService.signOut();
        clearAll();
        setOrphanedAccountMessage("We couldn't find your account setup. Please sign up again.");
        return null;
      }
    }
    setSignedOutByUser(false);
    setUser(u);
    setUserProfile(u.profile);
    setIsSuperAdmin(superAdmin);
    return u;
  }, [clearAll]);

  useEffect(() => {
    let cancelled = false;
    const unsub = onAuthStateChanged(auth, async (fb) => {
      try {
        if (!fb) {
          epoch.current++;
          clearAll();
        } else {
          await syncFromFirebase();
        }
      } catch (e) {
        if (__DEV__) console.error("Auth state sync error:", e);
        clearAll();
      } finally {
        if (!cancelled) setLoading(false);
      }
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, [clearAll, syncFromFirebase]);

  const applySession = useCallback(async (data) => {
    const mine = ++epoch.current;
    const fbUser = auth.currentUser;
    // The sign-in / sign-up call returned after the browser's account changed (another tab): the
    // auth observer owns the state for whoever is signed in now.
    if (!fbUser || fbUser.uid !== data.user?.uid) return;
    setSignedOutByUser(false);
    setUser({ ...data.user, profile: data.profile });
    setUserProfile(data.profile);
    const superAdmin = await readSuperAdmin(fbUser);
    // A newer auth change (sign-out, account switch in another tab) owns the role gate now.
    if (mine === epoch.current && auth.currentUser?.uid === fbUser.uid) setIsSuperAdmin(superAdmin);
  }, []);

  const signIn = useCallback(async (email, password, keepSignedIn = true) => {
    try {
      await setKeepSignedIn(keepSignedIn);
      const { data, error } = await AuthService.signIn(email, password);
      if (error) return { success: false, error: error.message };
      await applySession(data);
      AuthService.updateLastActive(data.user.uid).catch(() => {});
      return { success: true, error: null };
    } catch (e) {
      if (__DEV__) console.error("Sign in error:", e);
      return { success: false, error: UNEXPECTED };
    }
  }, [applySession]);

  const signUp = useCallback(async (email, password, userData) => {
    try {
      const { data, error } = await AuthService.signUp(email, password, userData);
      if (error) return { success: false, error: error.message };
      await applySession(data);
      return { success: true, error: null, uid: data.user?.uid || null, verificationEmailFailed: !!data.verificationEmailFailed };
    } catch (e) {
      if (__DEV__) console.error("Sign up error:", e);
      return { success: false, error: UNEXPECTED };
    }
  }, [applySession]);

  const signOut = useCallback(async () => {
    const { error } = await AuthService.signOut();
    epoch.current++;
    setSignedOutByUser(true);
    clearAll(); // local state always clears, even if Firebase sign-out reported an error
    return error ? { success: false, error: error.message } : { success: true, error: null };
  }, [clearAll]);

  const refreshProfile = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return { data: null, error: new Error("Not signed in") };
    const { data, error } = await AuthService.getUserProfile(uid);
    if (!error && data && auth.currentUser?.uid === uid) {
      setUserProfile(data);
      setUser((prev) => (prev && prev.uid === uid ? { ...prev, profile: data } : prev));
    }
    return { data, error };
  }, []);

  const checkVerification = useCallback(async () => {
    const { verified, error } = await AuthService.checkEmailVerification();
    if (error) return { verified: false, error: error.message };
    if (verified) await syncFromFirebase();
    return { verified, error: null };
  }, [syncFromFirebase]);

  const sendEmailVerification = useCallback(async () => {
    const { error } = await AuthService.sendEmailVerification();
    return error ? { success: false, error: error.message } : { success: true, error: null };
  }, []);

  const uid = user?.uid;
  const emailVerified = user?.emailVerified === true;
  useEffect(() => {
    if (!uid || emailVerified) return undefined;
    const id = setInterval(() => { checkVerification().catch(() => {}); }, 3000);
    return () => clearInterval(id);
  }, [uid, emailVerified, checkVerification]);

  const value = useMemo(() => ({
    user, userProfile, loading, emailVerified, isSuperAdmin, signedOutByUser,
    orphanedAccountMessage, clearOrphanedAccountMessage: () => setOrphanedAccountMessage(null),
    signIn, signUp, signOut, refreshProfile, checkVerification, sendEmailVerification,
  }), [user, userProfile, loading, emailVerified, isSuperAdmin, signedOutByUser, orphanedAccountMessage,
    signIn, signUp, signOut, refreshProfile, checkVerification, sendEmailVerification]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
